import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Checkbox, Dimmer, Dropdown, Icon, Input, Loader, Select, Table } from 'semantic-ui-react';
import { resetDeliveryAreasCache } from '../../../utils/deliveryAreas';
import '../list/list.scss';
import './delivery-zones.scss';

interface Zone {
  id: number
  name: string
  expressFee: number
  active: number
}

interface Area {
  id: number
  town: string
  district: string
  unit: string
  zoneId: number | null
}

interface ZoneDraft {
  name: string
  expressFee: string
  active: boolean
}

const NO_ZONE = '';

const toDraft = (zone: Zone): ZoneDraft => ({ name: zone.name, expressFee: String(zone.expressFee), active: zone.active === 1 });

function DeliveryZonesComponent() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [zones, setZones] = useState<Zone[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [drafts, setDrafts] = useState<Record<number, ZoneDraft>>({});
  const [newZone, setNewZone] = useState({ name: '', expressFee: '' });
  const [deletingZoneId, setDeletingZoneId] = useState<number | null>(null);
  // "add khoroo to a zone": pick a zone, a district, then any of that district's khoroo
  const [pick, setPick] = useState<{ zoneId: string, district: string, areaIds: number[] }>({ zoneId: '', district: '', areaIds: [] });
  const [freeMin, setFreeMin] = useState<number | null>(null);

  const call = useCallback(async (path: string, method: 'GET' | 'POST', body?: any) => {
    const token = localStorage.getItem('adminToken');
    if (!token || token === 'undefined') {
      window.alert('Та дахин нэвтэрнэ үү');
      history.push('/mongol-login');
      throw new Error('no admin token');
    }
    const res = await fetch(process.env.REACT_APP_API_URL + path, {
      method,
      headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 401) {
      history.push('/mongol-login');
      throw new Error('unauthorized');
    }
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, json };
  }, [history]);

  const load = useCallback(async () => {
    try {
      const { ok, json } = await call('mongol/delivery-zones', 'GET');
      if (!ok) {
        setErrorMsg(json.error || 'Серверт алдаа гарлаа');
        return;
      }
      setZones(json.zones);
      setAreas(json.areas);
      setDrafts(Object.fromEntries(json.zones.map((z: Zone) => [z.id, toDraft(z)])));
    } catch (err) {
      console.error(err);
      setErrorMsg('Сүлжээний алдаа');
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => {
    load();
    fetch(process.env.REACT_APP_API_URL + 'settings')
      .then((res) => res.json())
      .then((json) => {
        const row = (json.data || []).find((s: any) => s.key === 'EXPRESS_FREE_MIN_AMOUNT');
        if (row) setFreeMin(Number(row.value));
      })
      .catch((err) => console.error(err));
  }, [load]);

  /** Runs a mutation, then refreshes the lists (and the cache the storefront forms share). */
  const mutate = async (path: string, body: any, onDone?: () => void) => {
    setErrorMsg('');
    setSuccessMsg('');
    setLoading(true);
    try {
      const { ok, json } = await call(path, 'POST', body);
      if (!ok) {
        setErrorMsg(json.error || 'Серверт алдаа гарлаа');
        return;
      }
      resetDeliveryAreasCache();
      onDone && onDone();
      setSuccessMsg('Амжилттай хадгаллаа.');
      await load();
    } catch (err) {
      console.error(err);
      setErrorMsg('Сүлжээний алдаа');
    } finally {
      setLoading(false);
    }
  };

  const feeOf = (value: string) => (value.trim() === '' ? NaN : Number(value));
  const feeIsValid = (value: string) => Number.isInteger(feeOf(value)) && feeOf(value) >= 0;

  const saveZone = (zone: Zone) => {
    const draft = drafts[zone.id];
    if (!draft.name.trim()) {
      setErrorMsg('Бүсийн нэр оруулна уу');
      return;
    }
    if (!feeIsValid(draft.expressFee)) {
      setErrorMsg('Express үнэ буруу байна');
      return;
    }
    mutate('mongol/delivery-zone', { id: zone.id, name: draft.name.trim(), expressFee: Number(draft.expressFee), active: draft.active ? 1 : 0 });
  };

  const addZone = () => {
    if (!newZone.name.trim()) {
      setErrorMsg('Бүсийн нэр оруулна уу');
      return;
    }
    if (!feeIsValid(newZone.expressFee)) {
      setErrorMsg('Express үнэ буруу байна');
      return;
    }
    mutate('mongol/delivery-zone', { name: newZone.name.trim(), expressFee: Number(newZone.expressFee), active: 1 },
      () => setNewZone({ name: '', expressFee: '' }));
  };

  const assign = (zoneValue: string, target: { areaIds?: number[], district?: string }, onDone?: () => void) =>
    mutate('mongol/delivery-areas/zone', { zoneId: zoneValue === NO_ZONE ? null : Number(zoneValue), ...target }, onDone);

  const districts = useMemo(() => {
    const byDistrict = new Map<string, Area[]>();
    for (const area of areas) {
      const key = `${area.town}|${area.district}`;
      byDistrict.set(key, [...(byDistrict.get(key) || []), area]);
    }
    return Array.from(byDistrict.entries()).map(([key, list]) => ({ key, town: list[0].town, district: list[0].district, areas: list }));
  }, [areas]);

  const zoneCount = (zoneId: number) => areas.filter((a) => a.zoneId === zoneId).length;
  const zoneNameOf = (zoneId: number | null) => zones.find((z) => z.id === zoneId)?.name;
  const pickDistrictAreas = useMemo(
    () => (districts.find((d) => d.district === pick.district)?.areas || []),
    [districts, pick.district]);

  const addPickedToZone = () => {
    if (!pick.zoneId) {
      setErrorMsg('Бүс сонгоно уу');
      return;
    }
    if (pick.areaIds.length === 0) {
      setErrorMsg('Бүсэд нэмэх хороогоо сонгоно уу');
      return;
    }
    assign(pick.zoneId, { areaIds: pick.areaIds }, () => setPick((current) => ({ ...current, areaIds: [] })));
  };
  const zoneOptions = (extra?: React.ReactNode) => (
    <>
      <option value={NO_ZONE}>Бүсгүй</option>
      {zones.map((z) => <option key={z.id} value={String(z.id)}>{z.name}{z.active === 1 ? '' : ' (идэвхгүй)'}</option>)}
      {extra}
    </>
  );

  return (
    <section className="full-width delivery-zones-page">
      <Dimmer active={loading} inverted>
        <Loader size="medium" />
      </Dimmer>
      <h2>Хүргэлтийн бүс (Express)</h2>
      {errorMsg ? <div className="error-msg" data-testid="zones-error">{errorMsg}</div> : null}
      {successMsg ? <div className="success-msg" data-testid="zones-success">{successMsg}</div> : null}

      <p className="delivery-zones-hint">
        Express хүргэлтийн үнэ бүсээр тогтоогдоно. Захиалгын дүн{' '}
        <strong data-testid="zones-free-min">{freeMin === null ? '—' : freeMin.toLocaleString('en-US') + '₮'}</strong>
        {' '}болон түүнээс дээш бол express үнэгүй. Энэ дүнг <Link to="/mongol/settings" className="underline hover-blue">Веб сайтын тохиргоо</Link>{' '}
        дотор <code>EXPRESS_FREE_MIN_AMOUNT</code> гэсэн мөрөөс өөрчилнө.
      </p>

      <h3>Бүсүүд</h3>
      <Table striped className="admin-list-table" width={16}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Бүсийн нэр</Table.HeaderCell>
            <Table.HeaderCell>Express үнэ (₮)</Table.HeaderCell>
            <Table.HeaderCell>Идэвхтэй</Table.HeaderCell>
            <Table.HeaderCell>Хороо</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {zones.map((zone) => {
            const draft = drafts[zone.id] || toDraft(zone);
            const changed = JSON.stringify(draft) !== JSON.stringify(toDraft(zone));
            const setDraft = (patch: Partial<ZoneDraft>) => setDrafts({ ...drafts, [zone.id]: { ...draft, ...patch } });
            return (
              <Table.Row key={zone.id} data-testid={`zone-row-${zone.id}`}>
                <Table.Cell data-label="Бүсийн нэр">
                  <Input data-testid={`zone-name-${zone.id}`} value={draft.name} onChange={(e) => setDraft({ name: e.target.value })} />
                </Table.Cell>
                <Table.Cell data-label="Express үнэ (₮)">
                  <Input data-testid={`zone-fee-${zone.id}`} type="number" min="0" value={draft.expressFee} onChange={(e) => setDraft({ expressFee: e.target.value })} />
                </Table.Cell>
                <Table.Cell data-label="Идэвхтэй">
                  <Checkbox data-testid={`zone-active-${zone.id}`} checked={draft.active} onChange={(e, data) => setDraft({ active: Boolean(data.checked) })} />
                </Table.Cell>
                <Table.Cell data-label="Хороо">{zoneCount(zone.id)}</Table.Cell>
                <Table.Cell className="delivery-zones-actions">
                  <Button primary size="small" data-testid={`zone-save-${zone.id}`} disabled={!changed} onClick={() => saveZone(zone)}>Хадгалах</Button>
                  {deletingZoneId === zone.id ? (
                    <>
                      <Button negative size="small" data-testid={`zone-delete-confirm-${zone.id}`}
                        onClick={() => mutate('mongol/delivery-zone/delete', { id: zone.id }, () => setDeletingZoneId(null))}>Баталгаажуулах</Button>
                      <Button size="small" onClick={() => setDeletingZoneId(null)}>Болих</Button>
                    </>
                  ) : (
                    <Button basic size="small" data-testid={`zone-delete-${zone.id}`} onClick={() => setDeletingZoneId(zone.id)}>
                      <Icon name="trash alternate outline" />Устгах
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
          <Table.Row>
            <Table.Cell data-label="Бүсийн нэр">
              <Input data-testid="zone-name-new" placeholder="Шинэ бүсийн нэр" value={newZone.name} onChange={(e) => setNewZone({ ...newZone, name: e.target.value })} />
            </Table.Cell>
            <Table.Cell data-label="Express үнэ (₮)">
              <Input data-testid="zone-fee-new" type="number" min="0" placeholder="0" value={newZone.expressFee} onChange={(e) => setNewZone({ ...newZone, expressFee: e.target.value })} />
            </Table.Cell>
            <Table.Cell />
            <Table.Cell />
            <Table.Cell>
              <Button color="green" size="small" data-testid="zone-add" onClick={addZone}><Icon name="plus" />Бүс нэмэх</Button>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      <h3>Бүсэд хороо нэмэх</h3>
      <p className="delivery-zones-hint">
        Бүс болон дүүргээ сонгоод, тухайн дүүргийн хорооноос (хэд ч байж болно) сонгож бүсэд нэмнэ. Өөр бүсэд байгаа хороог сонговол шинэ бүс рүү шилжинэ.
      </p>
      <div className="delivery-zones-pick" data-testid="zone-pick">
        <Select
          data-testid="pick-zone"
          placeholder="Бүс сонгох"
          value={pick.zoneId}
          options={zones.map((z) => ({ key: z.id, value: String(z.id), text: z.name + (z.active === 1 ? '' : ' (идэвхгүй)') }))}
          onChange={(e, data) => setPick({ ...pick, zoneId: String(data.value) })}
        />
        <Select
          data-testid="pick-district"
          placeholder="Дүүрэг сонгох"
          search
          value={pick.district}
          options={districts.map((d) => ({ key: d.key, value: d.district, text: d.district }))}
          onChange={(e, data) => setPick({ ...pick, district: String(data.value), areaIds: [] })}
        />
        <Dropdown
          data-testid="pick-units"
          className="delivery-zones-pick-units"
          placeholder={pick.district ? 'Хороо сонгох' : 'Эхлээд дүүрэг сонгоно уу'}
          multiple
          search
          selection
          disabled={!pick.district}
          value={pick.areaIds}
          options={pickDistrictAreas.map((a) => ({
            key: a.id,
            value: a.id,
            text: a.unit,
            description: a.zoneId === null ? undefined : zoneNameOf(a.zoneId),
          }))}
          onChange={(e, data) => setPick({ ...pick, areaIds: (data.value as number[]) || [] })}
        />
        <Button basic data-testid="pick-select-all" disabled={!pick.district}
          onClick={() => setPick({ ...pick, areaIds: pickDistrictAreas.map((a) => a.id) })}>Бүгдийг сонгох</Button>
        <Button color="green" data-testid="pick-add" onClick={addPickedToZone}><Icon name="plus" />Бүсэд нэмэх</Button>
      </div>

      <h3>Дүүрэг, хороо</h3>
      <p className="delivery-zones-hint">
        Хороо бүрийг бүсэд онооно. Бүсгүй хороо руу express хүргэлт хийхгүй. Хорооны жагсаалт тогтмол: бүртгэл болон захиалгын хаягийг хэрэглэгч зөвхөн эндээс сонгоно.
      </p>
      {districts.map(({ key, town, district, areas: list }) => {
        const assigned = list.filter((a) => a.zoneId !== null).length;
        return (
          <details key={key} className="delivery-zones-district" data-testid={`district-${district}`}>
            <summary>
              <strong>{district}</strong> <span className="delivery-zones-muted">({town}, {list.length} хороо, {assigned} нь бүстэй)</span>
            </summary>
            <div className="delivery-zones-district-body">
              <label>
                Бүх хороог нэг бүсэд оноох:{' '}
                <select data-testid={`district-zone-${district}`} value="__none__" onChange={(e) => assign(e.target.value, { district })}>
                  <option value="__none__" disabled>Бүс сонгох…</option>
                  {zoneOptions()}
                </select>
              </label>
              <div className="delivery-zones-units">
                {list.map((area) => (
                  <div className="delivery-zones-unit" key={area.id}>
                    <span>{area.unit}</span>
                    <select data-testid={`area-zone-${area.id}`} value={area.zoneId === null ? NO_ZONE : String(area.zoneId)}
                      onChange={(e) => assign(e.target.value, { areaIds: [area.id] })}>
                      {zoneOptions()}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </details>
        );
      })}
    </section>
  );
}

export default DeliveryZonesComponent;
